// -----------------------------------------------------------------------------
import { parseApiDate } from 'src/utils/taskDateUtils';
// checklistViewModel
//
// Pure-function view-model helpers for checklist task/item state logic.
// Consolidates logic previously duplicated across TaskChecklistCard,
// useV2ChecklistAutocomplete, TaskChecklistField, useGuidedChecklist, and
// useV2ChecklistContactedVia into a single leaf module with no React / heavy
// dependencies so it can run in isolation (tests, workers, etc.).
//
// This module absorbs the exports of checklistCurrentStatusFilter.js;
// that file now re-exports buildTemplateStatusMap + filterGroupsByCurrentStatus
// from here for backward compatibility — do NOT import from both.
//
// Divergences are documented per-function below and covered by tests.
// -----------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Template-status map (absorbed from checklistCurrentStatusFilter.js)
// ---------------------------------------------------------------------------

/**
 * Build template_id -> applies_to_statuses[] map from a task-templates payload.
 * @param {object[]} [tplData=[]] - rows from /task-templates/
 * @returns {Map<string, string[]>}
 */
export function buildTemplateStatusMap(tplData = []) {
    const map = new Map();
    (tplData || []).forEach((t) => {
        map.set(String(t.id), Array.isArray(t.applies_to_statuses) ? t.applies_to_statuses : []);
    });
    return map;
}

/**
 * Drop tasks whose template no longer applies to their treatment's current
 * status, then drop any group left with no tasks.
 *
 * Fail-open semantics:
 *   - Empty/missing map → return groups untouched.
 *   - Unknown template_id or empty applies_to_statuses → keep group untouched.
 *   - task.lead_treatment == null (ad-hoc) → keep.
 *   - task.lead_treatment.status falsy → keep.
 *
 * @param {object[]} groups       - groups from /task-checklists/
 * @param {Map}      tplStatusMap - template_id -> applies_to_statuses[]
 * @returns {object[]}
 */
export function filterGroupsByCurrentStatus(groups, tplStatusMap) {
    // No usable map (templates not loaded / fetch failed) -> fail open.
    if (!tplStatusMap || tplStatusMap.size === 0) return groups || [];

    return (groups || [])
        .map((g) => {
            const statuses = tplStatusMap.get(String(g.template_id));
            // Unknown template or no targeting -> keep the group untouched.
            if (!statuses || statuses.length === 0) return g;

            const tasks = (g.tasks || []).filter((task) => {
                const lt = task.lead_treatment;
                if (lt == null) return true;        // ad-hoc / unscoped -> keep
                const ltStatus = lt.status;
                if (!ltStatus) return true;         // no status info -> keep
                return statuses.includes(ltStatus); // keep only if template applies to current status
            });

            return { ...g, tasks };
        })
        .filter((g) => (g.tasks || []).length > 0);
}

// ---------------------------------------------------------------------------
// Task-level status helpers
// ---------------------------------------------------------------------------

/**
 * Returns true if the task is considered "open" (actionable).
 * Statuses: 'pending' | 'in_progress'
 * Source: TaskChecklistCard
 *
 * @param {object|null|undefined} task
 * @returns {boolean}
 */
export function isTaskOpen(task) {
    const s = String(task?.status || '').toLowerCase();
    return s === 'pending' || s === 'in_progress';
}

/**
 * Returns true if the task is considered "done" (not actionable).
 * Statuses: 'completed' | 'archived' | 'paused'
 * Note: 'paused' counts as done (not the same as 'pending'/'in_progress').
 * Source: inverse of isTaskOpen — but note 'paused' is deliberately included here.
 *
 * @param {object|null|undefined} task
 * @returns {boolean}
 */
export function isTaskDone(task) {
    const s = String(task?.status || '').toLowerCase();
    return s === 'completed' || s === 'archived' || s === 'paused';
}

// ---------------------------------------------------------------------------
// Checklist-item state helpers
// ---------------------------------------------------------------------------

// NOTE — QUEUED completion_state:
// 'QUEUED' is a CLIENT-SIDE TRANSIENT completion_state set by
// useV2ChecklistAutocomplete for items that are queued-for-completion-but-not-yet-
// committed (committed on "Finish"). It is treated as still-OPEN / not-done by
// these helpers — never as a terminal/done state.

/**
 * Returns true if a checklist item is considered done via ANY of:
 *   - item.completed_at != null
 *   - item.status === 'completed'
 *   - item.completion_state in ['YES', 'COMPLETE', 'SKIPPED', 'PAUSED']
 *
 * Note: 'QUEUED' is intentionally NOT in the done-set — it is still-OPEN/not-committed.
 * Caller is responsible for task-level cascade separately.
 * Source: useV2ChecklistAutocomplete.isItemDone
 *
 * @param {object|null|undefined} item
 * @returns {boolean}
 */
export function isChecklistItemDone(item) {
    if (!item) return false;
    if (item.completed_at != null) return true;
    if (item.status === 'completed') return true;
    const cs = item.completion_state;
    // QUEUED is client-side transient and intentionally excluded from the done-set.
    return cs === 'YES' || cs === 'COMPLETE' || cs === 'SKIPPED' || cs === 'PAUSED';
}

/**
 * NORMALIZING completion-state resolver (from useV2ChecklistAutocomplete).
 *
 * Maps item completion_state/completed_at/status to a canonical output:
 *   completion_state === 'PAUSED'    → 'PAUSED'
 *   completion_state === 'SKIPPED'   → 'SKIPPED'
 *   completion_state === 'YES'       → 'YES'
 *   completion_state === 'COMPLETE'  → 'YES'   (normalised)
 *   completed_at != null             → 'YES'
 *   status === 'completed'           → 'YES'
 *   else (includes 'NO', null, '')   → 'OPEN'
 *
 * @param {object|null|undefined} item
 * @returns {'PAUSED'|'SKIPPED'|'YES'|'OPEN'}
 */
export function resolveCompletionState(item) {
    if (!item) return 'OPEN';
    const cs = item.completion_state;
    if (cs === 'QUEUED') return 'OPEN'; // QUEUED is client-side transient — treat as still-OPEN, not committed
    if (cs === 'PAUSED') return 'PAUSED';
    if (cs === 'SKIPPED') return 'SKIPPED';
    if (cs === 'YES') return 'YES';
    if (cs === 'COMPLETE') return 'YES';
    if (item.completed_at != null) return 'YES';
    if (item.status === 'completed') return 'YES';
    return 'OPEN';
}

/**
 * MINIMAL completion-state getter (from TaskChecklistField.getCompletionState).
 *
 * Deliberately does NOT check completed_at or status — only completion_state
 * and is_completed. Keep this divergence; both variants are in use.
 *
 *   item.completion_state truthy → return verbatim
 *   item.is_completed            → 'YES'
 *   else                         → 'PENDING'
 *
 * @param {object|null|undefined} item
 * @returns {string}
 */
export function getChecklistItemCompletionState(item) {
    if (!item) return 'PENDING';
    if (item.completion_state) return item.completion_state;
    if (item.is_completed) return 'YES';
    return 'PENDING';
}

// ---------------------------------------------------------------------------
// Progress helpers
// ---------------------------------------------------------------------------

/**
 * Parse checklist progress from a lead/task row.
 *
 * Tries two sources in order:
 *   1. row.checklist_progress (string) matching "done / total" pattern.
 *   2. row.checklist_items (array) — counts items where is_completed || completed || completion_state==='YES'.
 * Returns null if neither source is available.
 *
 * @param {object|null|undefined} row
 * @returns {{done: number, total: number}|null}
 */
export function getChecklistProgress(row) {
    if (!row) return null;

    if (typeof row.checklist_progress === 'string') {
        const m = row.checklist_progress.match(/^\s*(\d+)\s*\/\s*(\d+)\s*$/);
        if (m) return { done: +m[1], total: +m[2] };
    }

    if (Array.isArray(row.checklist_items)) {
        const total = row.checklist_items.length;
        const done = row.checklist_items.filter(
            (i) => i.is_completed || i.completed || i.completion_state === 'YES'
        ).length;
        return { done, total };
    }

    return null;
}

// ---------------------------------------------------------------------------
// Group-level filters
// ---------------------------------------------------------------------------

/**
 * Filter checklist groups for the attended-consultation popup.
 * Applies three sequential gates per group; groups failing any gate are dropped.
 *
 * Gate A — status gate:
 *   statuses = tplStatusMap.get(String(g.template_id)) || []
 *   if !statuses.includes(attendedStatus) → drop group
 *
 * Gate B — treatment gate:
 *   tItems = tplTreatmentMap.get(String(g.template_id)) || []
 *   passesTemplate = tItems.length === 0
 *                 || (leadTreatmentId != null && tItems.some(ti => String(ti?.id) === String(leadTreatmentId)))
 *   if !passesTemplate → drop group
 *
 * Gate C — task-treatment gate:
 *   keep tasks where task.lead_treatment == null
 *                 || String(task.lead_treatment?.id) === String(matchedLeadTreatmentId)
 *   if no tasks left → drop group
 *
 * Source: useGuidedChecklist
 *
 * @param {object[]} groupsData
 * @param {object}   opts
 * @param {Map}      opts.tplStatusMap
 * @param {Map}      opts.tplTreatmentMap
 * @param {string|number|null} opts.leadTreatmentId
 * @param {string|number|null} opts.matchedLeadTreatmentId
 * @param {string}   [opts.attendedStatus='CONSULTATION_ATTENDED']
 * @returns {object[]}
 */
export function filterGroupsByTreatment(
    groupsData,
    {
        tplStatusMap,
        tplTreatmentMap,
        leadTreatmentId,
        matchedLeadTreatmentId,
        attendedStatus = 'CONSULTATION_ATTENDED',
    }
) {
    return (groupsData || [])
        .map((g) => {
            // Gate A — status gate
            const statuses = tplStatusMap.get(String(g.template_id)) || [];
            if (!statuses.includes(attendedStatus)) return null;

            // Gate B — treatment gate
            const tItems = tplTreatmentMap.get(String(g.template_id)) || [];
            const passesTemplate =
                tItems.length === 0 ||
                (leadTreatmentId != null &&
                    tItems.some((ti) => String(ti?.id) === String(leadTreatmentId)));
            if (!passesTemplate) return null;

            // Gate C — task-treatment gate
            const tasks = (g.tasks || []).filter(
                (task) =>
                    task.lead_treatment == null ||
                    String(task.lead_treatment?.id) === String(matchedLeadTreatmentId)
            );
            if (tasks.length === 0) return null;

            return { ...g, tasks };
        })
        .filter(Boolean);
}

// ---------------------------------------------------------------------------
// Flat item builders
// ---------------------------------------------------------------------------

/**
 * Flatten checklist groups → tasks → checklist_items into a single list,
 * promoting fields from task/group context onto each item.
 *
 * @param {object[]} groups
 * @param {object}   [options={}]
 * @param {boolean}  [options.normalize=false]        - resolve completion_state via resolveCompletionState
 * @param {string[]|null} [options.displayableTypes=null] - if provided, only include items whose
 *                                                         (item_type||action_type) is in the set
 * @param {boolean}  [options.withTemplateLabel=false] - if true AND lead has >1 distinct treatment
 *                                                       among groups, set _treatmentLabel from task.lead_treatment
 * @returns {object[]}
 */
export function buildFlatChecklistItems(groups, options = {}) {
    const { normalize = false, displayableTypes = null, withTemplateLabel = false } = options;

    // Determine whether multiple distinct treatments exist across all groups
    let multiTreatment = false;
    if (withTemplateLabel) {
        const treatmentIds = new Set();
        (groups || []).forEach((g) => {
            (g.tasks || []).forEach((t) => {
                if (t.lead_treatment != null) {
                    treatmentIds.add(String(t.lead_treatment.id ?? t.lead_treatment));
                }
            });
        });
        multiTreatment = treatmentIds.size > 1;
    }

    const items = [];
    let templateOrder = 0;

    (groups || []).forEach((g) => {
        (g.tasks || []).forEach((t) => {
            const taskStatusLower = String(t.status || '').toLowerCase();
            const taskCompleted =
                taskStatusLower === 'completed' || taskStatusLower === 'archived';

            (t.checklist_items || []).forEach((item) => {
                // Skip structural items (no item_type/action_type AND no title/name)
                const itemType = item.item_type || item.action_type || '';
                const resolvedTitle = item.title || item.name || itemType;
                if (!itemType && !item.title && !item.name) return;

                // displayableTypes filter
                if (displayableTypes !== null && !displayableTypes.includes(itemType)) return;

                const id = item.id ?? item.checklist_item_id;
                const templateItem =
                    item.template_item ??
                    item.template_item_id ??
                    item.template_checklist_item_id ??
                    null;

                const completionState = normalize
                    ? resolveCompletionState(item)
                    : item.completion_state;

                const done = isChecklistItemDone(item) || taskCompleted;

                const flat = {
                    id,
                    itemId: id,
                    taskId: t.id,
                    taskTitle: t.title,
                    templateId: g.template_id,
                    templateName: g.template_name || g.name,
                    item_type: itemType,
                    title: resolvedTitle,
                    description: item.description,
                    due_at: t.due_at || null,
                    paused_until: item.paused_until,
                    is_completed: item.is_completed,
                    completed_at: item.completed_at,
                    status: item.status,
                    sort_order: item.sort_order,
                    template_item: templateItem,
                    templateOrder,
                    completion_state: completionState,
                    done,
                    raw: item,
                    rawTask: t,
                };

                templateOrder += 1;

                if (withTemplateLabel) {
                    let treatmentLabel;
                    if (multiTreatment && t.lead_treatment) {
                        treatmentLabel =
                            t.lead_treatment.treatment_name ||
                            t.lead_treatment.name ||
                            t.lead_treatment;
                    }
                    flat._treatmentLabel = treatmentLabel;
                }

                items.push(flat);
            });
        });
    });

    return items;
}

/**
 * Sort flat checklist items into today/later/done buckets.
 *
 * - done   : items where item.done === true
 * - today  : not-done, due_at present and <= endOfTodayMs (covers overdue + today)
 * - later  : not-done, no due_at OR due_at > endOfTodayMs
 * - today is sorted by due_at ASC
 *
 * endOfTodayMs MUST be passed in — do NOT call Date.now() inside this function
 * so it remains pure and deterministic in tests.
 *
 * @param {object[]} items
 * @param {object}   opts
 * @param {number}   opts.endOfTodayMs - epoch ms for end of "today"
 * @returns {{today: object[], later: object[], done: object[]}}
 */
export function buildChecklistBuckets(items, { endOfTodayMs }) {
    const done = [];
    const today = [];
    const later = [];

    (items || []).forEach((item) => {
        if (item.done) {
            done.push(item);
            return;
        }
        if (item.due_at) {
            const dueMs = new Date(parseApiDate(item.due_at)).getTime();
            if (dueMs <= endOfTodayMs) {
                today.push(item);
            } else {
                later.push(item);
            }
        } else {
            later.push(item);
        }
    });

    today.sort((a, b) => new Date(parseApiDate(a.due_at)).getTime() - new Date(parseApiDate(b.due_at)).getTime());

    return { today, later, done };
}

/**
 * Build a map of open items grouped by item_type.
 *
 * Includes only items where:
 *   - resolveCompletionState(item) === 'OPEN' (or item.completion_state === 'OPEN' if pre-normalized)
 *   - (item.item_type || '') is in autoCompleteTypes
 *
 * Source: useV2ChecklistContactedVia / useV2ChecklistAutocomplete
 *
 * @param {object[]} items
 * @param {object}   opts
 * @param {string[]} opts.autoCompleteTypes
 * @returns {Object.<string, object[]>}
 */
export function buildOpenItemsByType(items, { autoCompleteTypes }) {
    const result = {};

    (items || []).forEach((item) => {
        // Use resolveCompletionState to determine openness
        const state = resolveCompletionState(item);
        if (state !== 'OPEN') return;

        const type = item.item_type || '';
        if (!autoCompleteTypes.includes(type)) return;

        if (!result[type]) result[type] = [];
        result[type].push(item);
    });

    return result;
}
